import type { ConnectionSkillDetailResponse, ConnectionSkillListResponse } from './connections.js';
import { z } from 'zod';

export const REMOTE_BETA_PROFILE = 'remote-beta-skills-read' as const;
export const REMOTE_BETA_SCOPE = ['skills.read'] as const;
// prettier-ignore
export const REMOTE_BETA_SKILLS_READ_TOOLS = ['skillspec_list_skills', 'skillspec_get_skill'] as const;
export const REMOTE_BETA_RESOURCES = [] as const;
export const REMOTE_BETA_PROMPTS = [] as const;

export type RemoteBetaSkillTool = (typeof REMOTE_BETA_SKILLS_READ_TOOLS)[number];

export const remoteBetaListArgumentsSchema = z
  .object({
    search: z.string().optional(),
    cursor: z
      .string()
      .regex(/^[1-9][0-9]*$/)
      .optional(),
    limit: z.number().int().min(1).max(50).optional(),
  })
  .strict();

export const remoteBetaDetailArgumentsSchema = z
  .object({ skillId: z.string().regex(/^[1-9][0-9]*$/) })
  .strict();

export type RemoteBetaClientDisposition = 'remote-beta' | 'legacy' | 'deny';

/**
 * One closed named-client decision shared by every OAuth and MCP boundary.
 * A beta-profile identity, or the configured opaque identity, can never
 * downgrade to predecessor behavior when topology is disabled or incoherent.
 */
export function remoteBetaClientDisposition(input: {
  enabled: boolean;
  configuredClientMatches: boolean;
  profile: string;
}): RemoteBetaClientDisposition {
  const isBetaProfile = input.profile === REMOTE_BETA_PROFILE;
  if (!isBetaProfile && !input.configuredClientMatches) return 'legacy';
  return input.enabled && isBetaProfile && input.configuredClientMatches ? 'remote-beta' : 'deny';
}

/** Strict validation plus deterministic property order for cross-process HMAC. */
export function canonicalRemoteBetaSelector(
  method: RemoteBetaSkillTool,
  args: unknown
): Record<string, unknown> {
  if (method === 'skillspec_list_skills') {
    const value = remoteBetaListArgumentsSchema.parse(args ?? {});
    return {
      method,
      ...(value.search !== undefined ? { search: value.search } : {}),
      ...(value.cursor !== undefined ? { cursor: value.cursor } : {}),
      ...(value.limit !== undefined ? { limit: value.limit } : {}),
    };
  }
  const value = remoteBetaDetailArgumentsSchema.parse(args);
  return { method, skillId: value.skillId };
}

export function assertRemoteBetaDiscoveryAllowlist(values: readonly string[]): void {
  if (
    values.length !== REMOTE_BETA_SKILLS_READ_TOOLS.length ||
    REMOTE_BETA_SKILLS_READ_TOOLS.some((value, index) => values[index] !== value)
  ) {
    throw new Error('remote_beta_discovery_denied');
  }
}

export function assertRemoteBetaInvocationAllowed(
  value: string
): asserts value is RemoteBetaSkillTool {
  if (!(REMOTE_BETA_SKILLS_READ_TOOLS as readonly string[]).includes(value)) {
    throw new Error('remote_beta_capability_denied');
  }
}

export function assertExactRemoteBetaScope(scope: readonly string[]): void {
  if (scope.length !== 1 || scope[0] !== REMOTE_BETA_SCOPE[0]) {
    throw new Error('invalid_scope');
  }
}

export type RemoteConnectionSkillResult =
  | ConnectionSkillListResponse
  | ConnectionSkillDetailResponse;

export interface RemoteConnectionAssertionV1 {
  iss: string;
  aud: 'skillspec-api-remote-connection';
  iat: number;
  nbf: number;
  exp: number;
  jti: string;
  purpose: 'remote-connection-skill-read';
  version: 1;
  userId: number;
  workspaceId: number;
  clientId: string;
  connectionId: string;
  grantId: string;
  authorityEpochId: string;
  accessJti: string;
  profile: typeof REMOTE_BETA_PROFILE;
  scope: ['skills.read'];
  capability: RemoteBetaSkillTool;
  requestSelector: Record<string, unknown>;
  requestHash: string;
}
